import { useEffect, useState } from 'react'
import { Alert, Button, Card, Descriptions, Form, Input, InputNumber, Modal, Space, Switch, Table, Tag, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SettingOutlined } from '@ant-design/icons'
import {
  listPaymentMethods,
  updatePaymentMethodFeeSharing,
  updatePaymentMethodStatus,
} from '../../services/paymentMethods'
import { useMerchantStore } from '../../stores/merchantStore'
import type { MerchantPaymentMethod, MerchantUnboundPaymentMethod, PaymentMethod } from '../../types'

const { Title, Text } = Typography

const paymentMethodLabels: Record<PaymentMethod, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch'n Go eWallet",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  PAYPAY: 'PayPay',
  MERPAY: 'Merpay',
  AUPAY: 'au PAY',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  KAKAO_PAY: 'Kakao Pay',
  NAVER_PAY: 'Naver Pay',
  TOSS_PAY: 'Toss Pay',
  PAYCO: 'PAYCO',
  SAMSUNG_PAY: 'Samsung Pay',
  T_MONEY: 'T-money',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
  USDT_DIRECT: 'USDT Direct',
}

const businessModeLabels: Record<MerchantPaymentMethod['businessMode'], string> = {
  ONE_TIME: '一次性支付',
  SUBSCRIPTION: '订阅支付',
  OFF_SESSION: '自动充值',
}

type FeeSharingFormValues = {
  customerFeeRatePercent: number
  customerPerTransactionFee: number
  checkoutDisplayText?: string
}

type PaymentMethodRow =
  | (MerchantPaymentMethod & { kind: 'bound' })
  | (MerchantUnboundPaymentMethod & { kind: 'unbound' })

function formatRate(raw: string): string {
  return `${(Number(raw || 0) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

function formatUsd(raw: string | number): string {
  return `USD ${Number(raw || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function rateToPercent(raw: string): number {
  return Number((Number(raw || 0) * 100).toFixed(6))
}

function percentToRateString(value?: number | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '0'
  return (value / 100).toFixed(8).replace(/0+$/, '').replace(/\.$/, '') || '0'
}

export default function PaymentMethods() {
  const verified = useMerchantStore((state) => (
    state.merchant?.kycStatus === 'APPROVED' || state.merchant?.kybStatus === 'APPROVED'
  ))
  const [methods, setMethods] = useState<PaymentMethodRow[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingMethod, setUpdatingMethod] = useState<string | null>(null)
  const [editingMethod, setEditingMethod] = useState<MerchantPaymentMethod | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm<FeeSharingFormValues>()
  const customerFeeRatePercent = Form.useWatch('customerFeeRatePercent', form)
  const customerPerTransactionFee = Form.useWatch('customerPerTransactionFee', form)

  const totalFeeRatePercent = editingMethod ? rateToPercent(editingMethod.feeRate) : 0
  const totalPerTransactionFee = editingMethod ? Number(editingMethod.perTransactionFee || 0) : 0
  const merchantFeeRatePercent = Math.max(
    0,
    Number((totalFeeRatePercent - Number(customerFeeRatePercent ?? 0)).toFixed(6))
  )
  const merchantPerTransactionFee = Math.max(
    0,
    Number((totalPerTransactionFee - Number(customerPerTransactionFee ?? 0)).toFixed(6))
  )

  const fetchMethods = async () => {
    setLoading(true)
    try {
      const res = await listPaymentMethods()
      setMethods([
        ...res.data.paymentMethods.map((method) => ({ ...method, kind: 'bound' as const })),
        ...(res.data.unboundPaymentMethods ?? []).map((method) => ({ ...method, kind: 'unbound' as const })),
      ])
    } catch (error) {
      message.error(error instanceof Error ? error.message : '載入支付方式失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMethods()
  }, [])

  const replaceMethod = (updated: MerchantPaymentMethod) => {
    setMethods((prev) => prev.map((item) => (
      item.kind === 'bound' && item.bindingId === updated.bindingId ? { ...updated, kind: 'bound' } : item
    )))
  }

  const handleStatusChange = async (record: MerchantPaymentMethod, enabled: boolean) => {
    setUpdatingMethod(record.bindingId)
    try {
      const res = await updatePaymentMethodStatus(record.paymentMethod, enabled, record.bindingId)
      replaceMethod(res.data)
      message.success(enabled ? '支付方式已啟用' : '支付方式已禁用')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新支付方式狀態失敗')
    } finally {
      setUpdatingMethod(null)
    }
  }

  const openFeeSharingModal = (record: MerchantPaymentMethod) => {
    setEditingMethod(record)
    form.setFieldsValue({
      customerFeeRatePercent: rateToPercent(record.customerFeeRate),
      customerPerTransactionFee: Number(record.customerPerTransactionFee || 0),
      checkoutDisplayText: record.checkoutDisplayText || undefined,
    })
    setModalOpen(true)
  }

  const handleSubmitFeeSharing = async (values: FeeSharingFormValues) => {
    if (!editingMethod) return
    setUpdatingMethod(editingMethod.bindingId)
    try {
      const res = await updatePaymentMethodFeeSharing(editingMethod.paymentMethod, {
        customerFeeRate: percentToRateString(values.customerFeeRatePercent),
        customerPerTransactionFee: String(values.customerPerTransactionFee ?? 0),
        checkoutDisplayText: values.checkoutDisplayText?.trim() || undefined,
        bindingId: editingMethod.bindingId,
      })
      replaceMethod(res.data)
      setModalOpen(false)
      setEditingMethod(null)
      message.success('手续费承担方式已更新')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新手续费承担方式失败')
    } finally {
      setUpdatingMethod(null)
    }
  }

  const columns: ColumnsType<PaymentMethodRow> = [
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: PaymentMethod) => paymentMethodLabels[method] || method,
    },
    {
      title: '业务场景',
      dataIndex: 'businessMode',
      key: 'businessMode',
      render: (mode: MerchantPaymentMethod['businessMode']) => businessModeLabels[mode] || mode,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (_, record) => record.kind === 'bound' ? (
        <Tag color={record.enabled ? 'green' : 'default'}>{record.enabled ? '已启用' : '已禁用'}</Tag>
      ) : (
        <Space direction="vertical" size={0}>
          <Tag color="orange">未开通</Tag>
          {!record.channelEnabled && <Text type="secondary">平台暂不可用</Text>}
        </Space>
      ),
    },
    {
      title: '订单手续费',
      key: 'fee',
      render: (_, record) => record.kind === 'unbound' ? '—' : (
        <Space direction="vertical" size={0}>
          <Text>{formatRate(record.feeRate)}</Text>
          <Text type="secondary">{formatUsd(record.perTransactionFee)} / 笔</Text>
        </Space>
      ),
    },
    {
      title: '客户承担',
      key: 'customerShare',
      render: (_, record) => record.kind === 'unbound' ? '—' : (
        <Space direction="vertical" size={0}>
          <Text>{formatRate(record.customerFeeRate)}</Text>
          <Text type="secondary">{formatUsd(record.customerPerTransactionFee)} / 笔</Text>
        </Space>
      ),
    },
    {
      title: '商户承担',
      key: 'merchantShare',
      render: (_, record) => record.kind === 'unbound' ? '—' : (
        <Space direction="vertical" size={0}>
          <Text>{formatRate(record.merchantFeeRate)}</Text>
          <Text type="secondary">{formatUsd(record.merchantPerTransactionFee)} / 笔</Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      render: (_, record) => record.kind === 'unbound' ? (
        <Text type="secondary">{verified ? '请联系客服开通' : '先通过 KYB / KYC'}</Text>
      ) : (
        <Space>
          <Switch
            checked={record.enabled}
            loading={updatingMethod === record.bindingId}
            onChange={(checked) => handleStatusChange(record, checked)}
          />
          <Button
            icon={<SettingOutlined />}
            loading={updatingMethod === record.bindingId}
            onClick={() => openFeeSharingModal(record)}
          >
            设置手续费承担方式
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>支付方式</Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="展示平台配置的所有支付方式。非默认开通的支付方式需通过 KYB / KYC 审核后，联系客服开通（support@kyrenpay.com）。已开通方式的手续费承担设置会影响客户在 Checkout 页面看到的应付金额。"
      />
      <Card>
        <Table
          rowKey={(record) => record.kind === 'bound' ? `binding:${record.bindingId}` : `unbound:${record.paymentMethod}:${record.businessMode}`}
          loading={loading}
          columns={columns}
          dataSource={methods}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: '暂无支付方式' }}
        />
      </Card>

      <Modal
        title={editingMethod ? `设置 ${paymentMethodLabels[editingMethod.paymentMethod]} 手续费承担方式` : '设置手续费承担方式'}
        open={modalOpen}
        footer={null}
        onCancel={() => {
          if (updatingMethod) return
          setModalOpen(false)
          setEditingMethod(null)
        }}
      >
        <Form<FeeSharingFormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmitFeeSharing}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="customerFeeRatePercent"
            label="客户承担费率 (%)"
            rules={[
              { required: true, message: '请输入客户承担费率' },
              () => ({
                validator(_, value) {
                  if (Number(value ?? 0) <= totalFeeRatePercent) return Promise.resolve()
                  return Promise.reject(new Error('客户承担费率不能高于订单手续费率'))
                },
              }),
            ]}
          >
            <InputNumber min={0} max={totalFeeRatePercent} step={0.01} precision={4} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="商户承担费率 (%)">
            <InputNumber value={merchantFeeRatePercent} precision={4} disabled style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="customerPerTransactionFee"
            label="客户承担每笔固定手续费 (USD)"
            rules={[
              { required: true, message: '请输入客户承担每笔固定手续费' },
              () => ({
                validator(_, value) {
                  if (Number(value ?? 0) <= totalPerTransactionFee) return Promise.resolve()
                  return Promise.reject(new Error('客户承担每笔固定手续费不能高于订单每笔固定手续费'))
                },
              }),
            ]}
          >
            <InputNumber min={0} max={totalPerTransactionFee} step={0.01} precision={4} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="商户承担每笔固定手续费 (USD)">
            <InputNumber value={merchantPerTransactionFee} precision={4} disabled style={{ width: '100%' }} />
          </Form.Item>
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="订单手续费率">{totalFeeRatePercent.toFixed(4)}%</Descriptions.Item>
            <Descriptions.Item label="订单每笔固定手续费">{formatUsd(totalPerTransactionFee)}</Descriptions.Item>
          </Descriptions>
          <Form.Item name="checkoutDisplayText" label="Checkout 展示文案">
            <Input.TextArea rows={2} maxLength={80} showCount placeholder="例如：支付处理费" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={Boolean(updatingMethod)}>
                保存
              </Button>
              <Button
                disabled={Boolean(updatingMethod)}
                onClick={() => {
                  setModalOpen(false)
                  setEditingMethod(null)
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
